import { useEffect, useRef, useState } from "react";

const CFG = {
  token: "7b0be889f76bf9b8c2f369212282b4ff",
  src: "https://cdn.agentbot.net/core/latest/core.js?Myjagsd4wLjY===",
  maxRetries: 5,
  retryDelay: 1000,
  pollInterval: 150,
  pollTimeout: 8000
};

export default function AivoLoader() {
  const [status, setStatus] = useState("idle");
  const attemptRef = useRef(0);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);
  const startedRef = useRef(false);
  const hardFailRef = useRef(false);
  const initRef = useRef(false); // evita doble ejecución en StrictMode

  useEffect(() => {
    if (initRef.current || window.__AIVO_LOADER__) return;
    initRef.current = true;
    window.__AIVO_LOADER__ = true;

    normalizeConsole();
    startCycle();

    window.addEventListener("error", onScriptRuntimeError, true);
    return () => {
      window.removeEventListener("error", onScriptRuntimeError, true);
      clearInterval(pollRef.current);
      clearTimeout(timeoutRef.current);
      window.__AIVO_LOADER__ = false;
    };
  }, []);

  function normalizeConsole() {
    if (!window.console) window.console = {};
    
    // 1. Asegurar que existan funciones base
    ["log", "debug", "info", "warn", "error"].forEach((m) => {
      if (typeof window.console[m] !== "function") {
        window.console[m] = function () {};
      }
    });

    // 2. Asegurar bind
    if (!Function.prototype.bind) {
      Function.prototype.bind = function (ctx) {
        const self = this;
        return function () {
          return self.apply(ctx, arguments);
        };
      };
    }

    // 3. BLOQUEO AGRESIVO: Impedir que console.debug sea borrado
    try {
      // Guardamos una referencia segura a la función
      const safeDebug = window.console.debug;
      Object.defineProperty(window.console, "debug", {
        get: () => safeDebug,
        set: () => {}, // Ignorar intentos de sobrescritura
        configurable: false, // IMPIDE que sea borrado (delete console.debug fallará)
        enumerable: true
      });
      console.info("[AIVO] console.debug ha sido blindado contra borrado.");
    } catch (e) {
      console.warn("[AIVO] No se pudo blindar console.debug:", e);
    }
  }

  function startCycle() {
    if (startedRef.current || hardFailRef.current) return;
    removeOldScripts();
    setStatus("inject");
    injectScript();
  }

  function injectScript() {
    // Verificación previa
    console.info("[AIVO][console-check]", {
      debugType: typeof console.debug,
      isLocked: Object.getOwnPropertyDescriptor(window.console, 'debug')?.configurable === false
    });

    if (document.querySelector('script[data-aivo="true"]')) {
      beginPolling();
      return;
    }
    
    const s = document.createElement("script");
    // Agregamos timestamp para evitar caché del navegador que pueda traer una versión corrupta
    s.src = `${CFG.src}&attempt=${attemptRef.current}&ts=${Date.now()}`;
    s.async = true;
    s.dataset.aivo = "true";
    s.dataset.loaderAttempt = String(attemptRef.current);
    s.onload = beginPolling;
    s.onerror = () => handleFail(`error de red (intento ${attemptRef.current + 1})`);
    document.head.appendChild(s);
  }

  function beginPolling() {
    if (startedRef.current || hardFailRef.current) return;
    setStatus("polling");
    const start = Date.now();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (startedRef.current || hardFailRef.current) {
        clearInterval(pollRef.current);
        return;
      }
      if (window.$aivo && typeof window.$aivo.run === "function") {
        clearInterval(pollRef.current);
        safeRun();
      } else if (Date.now() - start > CFG.pollTimeout) {
        clearInterval(pollRef.current);
        handleFail("timeout esperando $aivo.run");
      }
    }, CFG.pollInterval);
  }

  function safeRun() {
    try {
      if (startedRef.current || hardFailRef.current) return;
      window.$aivo.run(CFG.token);
      startedRef.current = true;
      setStatus("running");
    } catch (e) {
      handleFail(`error ejecutando run: ${e && e.message}`);
    }
  }

  function handleFail(msg) {
    if (startedRef.current || hardFailRef.current) return;
    console.error("[AIVO]", msg);
    if (attemptRef.current + 1 >= CFG.maxRetries) {
      setStatus("max-retries");
      return;
    }
    setStatus("error");
    attemptRef.current++;
    timeoutRef.current = setTimeout(startCycle, CFG.retryDelay);
  }

  function onScriptRuntimeError(ev) {
    if (startedRef.current || hardFailRef.current) return;
    if (ev.target && ev.target.tagName === "SCRIPT" && ev.target.dataset.aivo === "true") {
      // Revalidar console.debug
      if (typeof console.debug !== "function") {
        console.debug = function(){ if (console.log) console.log.apply(console, arguments); };
      }
      // Intentar reinyección UNA sola vez adicional si aún no se agotaron reintentos
      if (attemptRef.current < CFG.maxRetries - 1) {
        handleFail("runtime error (bind) — reinyectando tras parche");
      } else {
        hardFailRef.current = true;
        setStatus("error");
        console.error("[AIVO] fallo interno persistente (bind) - abortando");
      }
    }
  }

  function removeOldScripts() {
    // Limpia sólo scripts rotos previos (si hay reinicio)
    document.querySelectorAll('script[data-aivo="true"]').forEach(n => {
      if (!startedRef.current) n.remove();
    });
  }

  return (
    <div style={{display:"none"}}
         data-aivo-status={status}
         data-aivo-attempt={attemptRef.current} />
  );
}